import { Info, ExternalLink } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/utils/cn'

// =============================================================================
// Legal Disclaimer Components
// Required for compliance with:
// - Anthropic Usage Policy (AI disclosure for high-risk legal use cases)
// - USPTO Bulk XML data attribution (public domain, 35 U.S.C. § 105)
// - EPO OPS Fair Use Charter
// =============================================================================

interface DisclaimerProps {
  className?: string
}

/**
 * Main AI Disclosure Banner
 * Required by Anthropic for consumer-facing legal applications
 * Shows that AI is used and requires human verification
 */
export function AIDisclosureBanner({ className }: DisclaimerProps) {
  return (
    <Alert className={cn('border-brand-info-bd bg-brand-info-bg', className)}>
      <Info className="h-4 w-4 text-brand-info" />
      <AlertTitle className="text-brand-info">AI-Assisted Analysis</AlertTitle>
      <AlertDescription className="text-brand-info text-sm">
        This platform uses Claude AI (Anthropic) to analyze patent relevance.
        AI analysis is provided as a research aid only and <strong>requires verification by a qualified patent professional</strong> before any legal decisions are made.
      </AlertDescription>
    </Alert>
  )
}

/**
 * Compact AI disclosure for inline use
 */
export function AIDisclosureInline({ className }: DisclaimerProps) {
  return (
    <div className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
      <Info className="h-3 w-3" />
      <span>AI-assisted analysis - requires professional verification</span>
    </div>
  )
}

/**
 * Report Disclaimer
 * For generated analysis reports
 */
export function ReportDisclaimer({ className }: DisclaimerProps) {
  return (
    <div className={cn('text-xs text-muted-foreground border-t pt-4 mt-4 space-y-2', className)}>
      <p className="font-medium">Disclaimer</p>
      <p>
        This report was generated with AI assistance (Claude by Anthropic) and is provided for
        informational and research purposes only. It does not constitute legal advice. All findings
        must be independently verified by a qualified patent professional before any filing or
        legal decisions are made. AI analysis may contain errors or omissions.
      </p>
      <p>
        Patents that claim a chemical compound through a structural formula, or a biological product
        through a sequence listing, are not reliably found by text and classification searching. If the
        product contains such a compound or sequence, a specialist structure or sequence search is
        required in addition to this report.
      </p>
    </div>
  )
}

/**
 * Data Sources Attribution
 * Required by USPTO attribution norms and EPO Fair Use Charter
 */
export function DataSourcesAttribution({ className }: DisclaimerProps) {
  return (
    <div className={cn('text-xs text-muted-foreground space-y-1', className)}>
      <p className="font-medium">Data Sources & Attribution</p>
      <ul className="space-y-1">
        <li className="flex items-center gap-1">
          <span>US patent data sourced from</span>
          <a
            href="https://bulkdata.uspto.gov"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-0.5"
          >
            USPTO Bulk XML data
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
          <span>(public domain, 35 U.S.C. § 105)</span>
        </li>
        <li className="flex items-center gap-1">
          <span>European patent data provided by</span>
          <a
            href="https://www.epo.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-0.5"
          >
            European Patent Office (EPO)
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
          <span>via Open Patent Services (OPS)</span>
        </li>
        <li className="flex items-center gap-1">
          <span>US patent-term data partly from</span>
          <a
            href="https://patentsview.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-0.5"
          >
            PatentsView
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
          <span>(CC BY 4.0)</span>
        </li>
        <li className="flex items-center gap-1">
          <span>AI analysis powered by</span>
          <a
            href="https://www.anthropic.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-0.5"
          >
            Claude (Anthropic)
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </li>
      </ul>
    </div>
  )
}

/**
 * Compact footer attribution for all pages
 */
export function FooterAttribution({ className }: DisclaimerProps) {
  return (
    <div className={cn('text-xs text-muted-foreground', className)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>
          Patent data:{' '}
          <a
            href="https://data.uspto.gov"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            USPTO
          </a>
          {' '}&amp; patent-term data from{' '}
          <a
            href="https://patentsview.org"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            PatentsView
          </a>
          {' '}(
          <a
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            CC BY 4.0
          </a>
          ) &{' '}
          <a
            href="https://www.epo.org"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            EPO OPS
          </a>
        </span>
        <span>|</span>
        <span>
          AI:{' '}
          <a
            href="https://www.anthropic.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            Claude (Anthropic)
          </a>
        </span>
        <span>|</span>
        <span className="text-brand-rust">
          Not legal advice - requires professional review
        </span>
      </div>
    </div>
  )
}

/**
 * Full legal disclaimer for reports and exports
 */
export function FullLegalDisclaimer({ className }: DisclaimerProps) {
  return (
    <div className={cn('text-sm space-y-4 p-4 border rounded-lg bg-muted/50', className)}>
      <h3 className="font-semibold">Legal Disclaimer & Data Attribution</h3>

      <div className="space-y-2">
        <h4 className="font-medium text-xs uppercase tracking-wide text-muted-foreground">AI Disclosure</h4>
        <p className="text-xs">
          This analysis was performed using Claude AI (Anthropic). AI-generated content is provided
          for informational and research purposes only. It does not constitute legal advice and may
          contain errors, omissions, or inaccuracies. All AI analysis requires independent verification
          by a qualified patent professional before any legal decisions or patent filings are made.
        </p>
      </div>

      <div className="space-y-2">
        <h4 className="font-medium text-xs uppercase tracking-wide text-muted-foreground">Data Sources</h4>
        <ul className="text-xs space-y-1">
          <li>
            <strong>USPTO Data:</strong> US patents (full text and classifications) come from the
            U.S. Patent and Trademark Office's public bulk data files (public domain, 35 U.S.C. § 105).
            Part of the patent-term data (term adjustments and parent applications) comes from
            PatentsView (patentsview.org), funded by the USPTO Office of the Chief Economist, licensed
            under Creative Commons Attribution 4.0 International (CC BY 4.0).
          </li>
          <li>
            <strong>European Patent Data:</strong> Provided by the European Patent Office (EPO)
            via Open Patent Services (OPS) API. Used in accordance with EPO Fair Use Charter.
          </li>
        </ul>
      </div>

      <div className="space-y-2">
        <h4 className="font-medium text-xs uppercase tracking-wide text-muted-foreground">Limitation of Liability</h4>
        <p className="text-xs">
          This tool is designed to assist patent professionals with FTO research.
          It is not a substitute for professional legal advice. Users are solely responsible
          for verifying all information and making their own legal determinations. The developers
          of this platform make no warranties regarding the accuracy, completeness, or fitness
          for any particular purpose of the information provided.
        </p>
      </div>
    </div>
  )
}

/**
 * User acknowledgment checkbox component
 * Required before starting AI analysis
 */
interface AcknowledgmentCheckboxProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  className?: string
}

export function AIAnalysisAcknowledgment({ checked, onCheckedChange, className }: AcknowledgmentCheckboxProps) {
  return (
    <div className={cn('p-4 border rounded-lg bg-muted/30 space-y-3', className)}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="ai-acknowledgment"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300"
        />
        <label htmlFor="ai-acknowledgment" className="text-sm cursor-pointer">
          <span className="font-medium">I understand and acknowledge that:</span>
          <ul className="mt-2 space-y-1 text-muted-foreground list-disc list-inside">
            <li>This analysis uses AI (Claude by Anthropic) and results require professional verification</li>
            <li>AI analysis is not legal advice and may contain errors or inaccuracies</li>
            <li>A qualified patent attorney should review all findings before any legal decisions</li>
            <li>Patent data is sourced from USPTO bulk data (with patent-term data from PatentsView) and EPO Open Patent Services</li>
            <li>Where patent claims are not available from the source API, a "No Claims" badge is shown — analysis then relies on title and abstract only, which reduces confidence since claims define the actual legal scope of protection</li>
            <li>US patent claims are available from 2005 to present — patents outside this range may appear without claims and will show a "No Claims" badge</li>
            <li>Recently filed patents may not yet be published and will not appear in results</li>
          </ul>
        </label>
      </div>
    </div>
  )
}
